import React, { useContext, useEffect } from 'react';
import {
    UploadDocumentation as UploadDocumentationType,
    FormValues,
    FormConfig,
    NumberOfFiles,
    Validator
} from '@topdanmark-onlinesolutions/claim-form-types';
import {
    Box,
    CheckboxField,
    RadioField,
    RadioInput,
    CheckboxGroup,
    IFile,
    ModalOverlay,
    ModalDialog,
    ModalHeader,
    ModalBody,
    Button
} from '@flos/react-ui';
import { v4 as uuid } from 'uuid';
import useAppState from '../../state/appState';
import FileUploadWrapper from '../FileUploadWrapper';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { StageFileResponse, StagedFile } from '../../types/services';
import { ConfigContext, TopConfig } from '../../context/ConfigContext';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { stageFiles } from '../../services/stageFiles';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getValue } from '../values/Get';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';
import { QRCodeSVG } from 'qrcode.react';
import { SavedForm } from '../../services/saveFormData';
import useService from '../../hooks/useService';
import { getSavedFormDataByFormIdServiceConfig } from '../../services/saveFormData';
import { useSearchParams } from 'react-router-dom';
import { trackInteract, trackInfo } from '../../track-by-submit/common/tracking';
import { useNavigate } from 'react-router-dom';
import { getPageIdFirst } from '../../helpers/getPageIdFirst';
import { buildUrlForQrCode } from '../../helpers/build-url-for-qr-code';
import { getUploadIsRequiredByFormIdAndValue } from '../../helpers/getUploadIsRequired';
import LoggerContext from '../../context/LoggerContext';

type UploadDocumentationValueType = {
    customerDidNotUploadReason: string;
    taskId: string | undefined;
    files: string[];
    isFileProcessing: boolean;
    pollIsActive?: boolean;
    stateUpdated: string;
    updateEvent: string;
};

export const UploadDocumentation = ({ elementId, caption, enableQrCode }: UploadDocumentationType) => {
    const formValues = useAppState((state) => state.formValues);
    const formConfig = useAppState((state) => state.formConfig);
    const setFormValue = useAppState((state) => state.setFormValue);
    const setFormValueWithNoSaveInStorage = useAppState((state) => state.setFormValueWithNoSaveInStorage);

    const value = UploadDocumentation.getValueOrDefault(formValues, elementId);
    const logger = useContext(LoggerContext);
    if (value === undefined) {
        throw new Error(`UploadDocumentation: Value for elementId ${elementId} is undefined`);
    }
    // Track if user leaves upload step without uploading files
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (formConfig && value?.files?.length === 0) {
                trackInfo(formConfig, [{ key: 'left-at-upload', value: true }]);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [formConfig, value]);

    const [search] = useSearchParams();

    const isFromDeeplink = search.get('deeplink') === 'true';

    const isUploadRequired = getUploadIsRequiredByFormIdAndValue(formConfig, formValues);

    const I_HAVE_NO_DOCTUMENTATION = 'Jeg har ingen dokumentation';
    const I_UPLOAD_LATER = 'Jeg uploader dokumentation senere';

    const topConfig = useContext(ConfigContext);

    const [c9UploadApiKey, setc9UploadApiKey] = React.useState<string>(uuid());
    const [isPolling, setIsPolling] = React.useState<boolean>(false);
    const [qrModalOpen, setQrModalOpen] = React.useState<boolean>(false);
    const [finishModalOpen, setFinishModalOpen] = React.useState<boolean>(false);

    const pollingIntervalInMs = 5000;

    const navigate = useNavigate();

    const { callService } = useService<SavedForm>(
        getSavedFormDataByFormIdServiceConfig(formConfig, topConfig),
        false,
        false
    );

    const handleUploadedFiles = (files: IFile[]) => {
        if (files.length > 0) {
            let fileNameList: string[] = files.flatMap((file) => (file.scanStatus === 'clean' ? [file.key] : []));
            setFormValue(elementId, {
                ...value,
                stateUpdated: Date.now(),
                updateEvent: 'HANDLE_UPLOADED_FILES_WITH_FILES',
                files: fileNameList,
                customerDidNotUploadReason: ''
            });
        } else {
            setFormValue(elementId, {
                ...value,
                updateEvent: 'HANDLE_UPLOADED_FILES_WITHOUT_FILES',
                stateUpdated: Date.now(),
                files: []
            });
        }
    };

    const handleIsFileProcessing = (status: boolean) => {
        setFormValue(elementId, {
            ...value,
            updateEvent: 'HANDLE_IS_FILE_PROCESSING',
            isFileProcessing: status
        });
    };

    const handleFileDeleted = (files: IFile[]) => {
        setFormValue(elementId, {
            ...value,
            updateEvent: 'HANDLE_FILE_DELETED',
            stateUpdated: Date.now(),
            files: files.map((file: IFile) => file.key)
        });
    };

    const onClickDoesNotWantToUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormValue(elementId, {
            ...value,
            customerDidNotUploadReason: e.target.checked ? I_HAVE_NO_DOCTUMENTATION : ''
        });
    };

    const onClickReasonNotToUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormValue(elementId, {
            ...value,
            customerDidNotUploadReason: e.target.value
        });
    };

    /**
     * Start polling manually by opening the QR code option
     */
    const setActivePollingInFormValue = () => {
        setFormValue(elementId, {
            ...value,
            pollIsActive: true
        });
    };

    // Polling logic: update formValues when data from claim-form-storage changes
    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;

        if (isPolling) {
            formConfig && trackInfo(formConfig, [{ key: 'qr-sync-started', value: true }]);

            intervalId = setInterval(() => {
                callService(undefined, false)
                    .then((data) => {
                        const storageValue: UploadDocumentationValueType = data && data.formValues[elementId];
                        const stateValue: UploadDocumentationValueType = formValues[elementId];
                        // If updated from storage is newer than state, update the state
                        // and change the c9-upload-api component key to force re-render of UploadService
                        if (
                            stateValue.stateUpdated < storageValue.stateUpdated &&
                            storageValue.taskId === stateValue.taskId
                        ) {
                            logger.info(
                                'Polling: stateValue is older than storageValue - updating state and trigger re-render of c9-upload-api'
                            );
                            setFormValueWithNoSaveInStorage(elementId, {
                                ...stateValue,
                                updateEvent: 'UPDATE_STATE_FROM_STORAGE',
                                files: storageValue.files,
                                stateUpdated: storageValue.stateUpdated
                            });
                            // Trigger a update of the c9-upload-api component

                            setc9UploadApiKey(uuid());
                            // Track QR-upload usage
                            if (formConfig) {
                                trackInfo(formConfig, [{ key: 'qr-upload-used', value: true }]);
                            }
                        } else {
                            // If taskId has been changed (user has restarted the form) and is not undefined,
                            // update the state with new taskId
                            if (storageValue.taskId !== stateValue.taskId && storageValue.taskId !== undefined) {
                                logger.info(
                                    'Polling: storageValue.taskId is different than stateValue.taskId - updating state with new taskId and trigger re-render of c9-upload-api'
                                );
                                setFormValueWithNoSaveInStorage(elementId, {
                                    ...stateValue,
                                    updateEvent: 'TASKID_RESETTED',
                                    stateUpdated: storageValue.stateUpdated,
                                    taskId: storageValue.taskId,
                                    files: storageValue.files,
                                    pollIsActive: true
                                });
                                // Trigger a update of the c9-upload-api component
                                setc9UploadApiKey(uuid());
                            }
                            // If storageValue is undefined the form may be deleted and the user should navigate to the first page
                            if ((storageValue === undefined || storageValue.taskId === undefined) && formConfig) {
                                logger.info(
                                    'Polling: storageValue and/or taskId is undefined - updating state and navigating to first page'
                                );
                                setFormValueWithNoSaveInStorage(elementId, {
                                    ...stateValue,
                                    updateEvent: 'STORAGE_VALUE_UNDEFINED',
                                    pollIsActive: true
                                });
                                navigate(getPageIdFirst(formConfig));
                            }
                        }
                    })
                    .catch((err) => {
                        if (err && err.name === 'CancelError') return;
                        console.error('callService error:', err);
                        formConfig && trackInfo(formConfig, [{ key: 'qr-sync-failed', value: true }]);
                    });
            }, pollingIntervalInMs);
        }
        return () => {
            if (intervalId) {
                // If user leaves before sync finishes
                formConfig && trackInfo(formConfig, [{ key: 'qr-sync-abandoned', value: true }]);
                clearInterval(intervalId);
            }
        };
    }, [
        formConfig,
        formValues,
        elementId,
        isPolling,
        value,
        setc9UploadApiKey,
        setFormValue,
        setFormValueWithNoSaveInStorage,
        callService,
        navigate,
        logger
    ]);

    useEffect(() => {
        /** Poll on mobile by deeplink - CURRENTLY DISABLED */
        // // If isFromDeeplink is true and isPolling is false, start polling
        // if (isFromDeeplink && !isPolling) {
        //     setIsPolling(true);
        //     setActivePollingInFormValue();
        // }

        // If formValues[elementId] has pollIsActive set to true and isPolling is false, start polling
        if (
            !isFromDeeplink &&
            value &&
            value.pollIsActive !== undefined &&
            value.pollIsActive === true &&
            isPolling === false
        ) {
            setIsPolling(true);
            setActivePollingInFormValue();
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    // Track when the radio is shown (only once)
    useEffect(() => {
        if (enableQrCode && formConfig) {
            trackInteract(formConfig, [{ key: 'mobile-upload-radio-shown', value: true }]);
        }
    }, [enableQrCode, formConfig]);

    /** This is only for experiemental use */
    const isIncludedInExperiment = () => {
        if (formConfig?.formId) {
            return [
                'parkeringsskade-privat',
                'afbestil-rejse-privat',
                'rejseskade-privat',
                'personskade-privat'
            ].includes(formConfig.formId);
        }
        return false;
    };

    const qrCodeUrl = buildUrlForQrCode(formConfig, elementId);
    console.log('QR Code URL:', qrCodeUrl);

    return (
        <div className="upload-documentation">
            {caption && <h6>{caption}</h6>}
            {isFromDeeplink && (
                <div className="u-spacing-stack-bottom-6">
                    Når du har uploadet billederne skal du vende tilbage til din computer for at fortsætte anmeldelsen.
                </div>
            )}
            <Box>
                <div key={'file-upload'} style={{ position: 'relative' }}>
                    {value.taskId && (
                        <FileUploadWrapper
                            handleUploadedFiles={handleUploadedFiles}
                            handleIsFileProcessing={handleIsFileProcessing}
                            handleFileDeleted={handleFileDeleted}
                            taskId={value.taskId}
                            c9uploadApiKey={c9UploadApiKey}
                        />
                    )}
                </div>

                <>
                    {isIncludedInExperiment() && !isUploadRequired ? (
                        <>
                            <RadioField
                                className={`${value.files.length > 0 && 'is-disabled'}`}
                                value={value.customerDidNotUploadReason}
                                disabled={value.files.length > 0}
                                onChange={onClickReasonNotToUpload}
                            >
                                <RadioInput label={I_HAVE_NO_DOCTUMENTATION} value={I_HAVE_NO_DOCTUMENTATION} />
                                <RadioInput label={I_UPLOAD_LATER} value={I_UPLOAD_LATER} />
                            </RadioField>
                            {isIncludedInExperiment() && (
                                <p className="small u-spacing-stack-top-4 u-color-secondary-4-medium">
                                    Når skaden er oprettet kan du tilføje dokumentation i Mit Topdanmark.
                                </p>
                            )}
                        </>
                    ) : (
                        <>
                            {!isUploadRequired && !isFromDeeplink && (
                                <CheckboxGroup
                                    required={value.files.length === 0 ? true : false}
                                    id={`${elementId}`}
                                    key={`checkbox-group-${value.customerDidNotUploadReason}`}
                                >
                                    <CheckboxField
                                        id={`${elementId}-checkbox`}
                                        key={`checkbox-iHaveNoDocumentation`}
                                        label={I_HAVE_NO_DOCTUMENTATION}
                                        value={I_HAVE_NO_DOCTUMENTATION}
                                        onChange={onClickDoesNotWantToUpload}
                                        checked={value.customerDidNotUploadReason === I_HAVE_NO_DOCTUMENTATION}
                                        disabled={value.files.length > 0}
                                    />
                                </CheckboxGroup>
                            )}
                        </>
                    )}
                </>

                {enableQrCode && (
                    <div className="only-show-on-desktop u-spacing-stack-top-4">
                        <RadioField value="">
                            <RadioInput
                                label="Upload fra mobil"
                                description="Hvis du har billederne liggende på din mobil, kan du uploade dem via en QR-kode."
                                value="yes"
                                onClick={() => {
                                    setQrModalOpen(true);
                                    setIsPolling(true);
                                    setActivePollingInFormValue();
                                    if (formConfig) {
                                        trackInteract(formConfig, [{ key: 'qr-upload-offered', value: true }]);
                                    }
                                }}
                            />
                        </RadioField>

                        <ModalOverlay
                            isOpen={qrModalOpen}
                            onDismiss={() => {
                                setQrModalOpen(false);
                            }}
                        >
                            <ModalDialog closeIconHandler={() => setQrModalOpen(false)}>
                                <ModalHeader title="Scan QR-koden med dit mobilkamera"></ModalHeader>
                                <ModalBody>
                                    <p>
                                        Du skal først scanne QR-koden med dit mobilkamera, og derefter uploade tre til
                                        fem billeder. Så snart du har uploadet billederne, er du færdig på mobilen og
                                        kan fortsætte anmeldelsen på din computer.
                                    </p>
                                    <p style={{ textAlign: 'center' }}>
                                        <QRCodeSVG value={qrCodeUrl} />
                                    </p>
                                </ModalBody>
                            </ModalDialog>
                        </ModalOverlay>
                    </div>
                )}
            </Box>
            {isFromDeeplink && (
                <div className="u-spacing-stack-top-6">
                    <Button onClick={() => setFinishModalOpen(true)}>Færdig</Button>
                    <ModalOverlay
                        isOpen={finishModalOpen}
                        onDismiss={() => {
                            setFinishModalOpen(false);
                        }}
                    >
                        <ModalDialog>
                            <ModalHeader title="Du kan nu lukke denne side"></ModalHeader>
                            <ModalBody>
                                <p>
                                    Du kan nu lukke denne side og vende tilbage til din computer for at gøre anmeldelsen
                                    færdig - vi hjælper dig hele vejen.
                                </p>
                            </ModalBody>
                        </ModalDialog>
                    </ModalOverlay>
                </div>
            )}
        </div>
    );
};

UploadDocumentation.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as UploadDocumentationType;
    const { summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    const { customerDidNotUploadReason, files } = value as UploadDocumentationValueType;

    return (
        <SummaryRowTemplate
            elementId={elementId}
            caption={summaryCaption}
            value={
                files.length > 0 ? (
                    files.map((fileName: string, i: number) => {
                        return (
                            <p title={fileName} key={`field-${i}`}>
                                {fileName.length > 20 ? (
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: fileName
                                                .split(/(.{20})/)
                                                .filter((O) => O)
                                                .join('<br/>')
                                        }}
                                    />
                                ) : (
                                    <span>{fileName}</span>
                                )}
                            </p>
                        );
                    })
                ) : (
                    <>{customerDidNotUploadReason}</>
                )
            }
        />
    );
};

UploadDocumentation.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    caption: string,
    fileData: StagedFile[]
): ClaimFormSubmitFieldValue => {
    const valueId = {
        files: fileData
    };

    const valueStr = UploadDocumentation.getValueHuman(formValues, elementId);
    return getClaimFormSubmitValue('file', elementId, caption, valueStr, valueId);
};

UploadDocumentation.stageFiles = async (formValues: FormValues, topConfig: TopConfig): Promise<StagedFile[]> => {
    // @TODO
    //   - This should be part of valueObj in state
    const taskIds = findTaskIds(formValues);

    if (!taskIds.length) {
        return [];
    }

    //    [
    //         {
    //             "osirFileId": "6f22e7c9-8391-45c6-b75a-5f330ff2a434",
    //             "url": "https://claim-form-stage-api-533042892064.s3.eu-west-1.amazonaws.com/876c3963-5b1c-453c-b9f6-a0377d5ddbb6/828b91b1-cc31-4fcb-aed2-a5d9a19e87d5/2845402/Snake_River_%285mb%29.jpg?AWSAccessKeyId=ASIAXYG6VBUQL7VI7W23&Expires=1691408976&Signature=reNzvQ%2BOCl9n%2BND5MlPSRGzXtX8%3D&X-Amzn-Trace-Id=Root%3D1-64d0d6cc-7ba43eed5ffe35b93a0d96d5%3BParent%3D0b4a530e8b8436df%3BSampled%3D1%3BLineage%3Dd9685e05%3A0&x-amz-security-token=IQoJb3JpZ2luX2VjEMz%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCWV1LXdlc3QtMSJIMEYCIQCk%2Fq7NMoELb4ZN5D5NrC18rVeVw5QCTwUv0gBKT0gjrQIhAOlBP1zYTm15iSteDIlF4lVfxdVcLq4IwPWUKNd4sV6pKpEDCHUQBBoMNTMzMDQyODkyMDY0IgzviyzpG4uDWwukhhcq7gIWffRQZhLgclZ2itGa%2B%2BM5KlQdQD2zvZCOXaSx6mp%2BqoyQ5s3P%2FPVoi1n5RLI7pvMiGDYHoxi6fMw1SC4iuYtpMHEsLQJioVjymFrVg1WgPuYMceavpjbOWEyNrJMmnRsmJnfeu38GHoNkiy3Xhnp3J5xES0DOYoz8sdP9gtj88huEAzB3R%2BR%2FfO0fSk4cglmgfX3O8kYwdWdLGqFbd6O1na4FqvBFxaiTsYFZs2DbQ2iFVm0f%2Fe27E8sepdXy3fAdknECtR%2FvKZPNQoPwSU32cl414Yb7vIVH%2BE8lkyXC%2Fchk07ITBt%2FGxlvAv48PK%2BuFa6IvURPDn%2BIivjxw8UioNkzg3dCuCerVe274djhdsKJgLBsxaL5luVjs8WhPAMP9BffK9ZPqTsE5JIYbyaJf8Uz7XYvwgZt%2F0rxMeDTqZDzzFvSyAlyEhamqkC%2Fmj5AYuiBtVo%2BphoW8wGPrPyiMscp9obgDyfGLM8rAcRYwq6vDpgY6nAFlQENIZU56B3wfTdhxL%2FdZUMcY4X7xATkniKJcD%2FMHtgReqUSmBOdx5RBtfqTD1h7SP2Vs5eVOUhV5Z0XW8DqxV0Cq5H2XKRLiy3a1qYhjyjPWNFtuGmvcAUoiIwVezzQxKTyVc0S1z6P7uY1v9iPrBJ9Ss06udkgDBNSQyE2k3%2FN2eWfL9%2B%2BltWcd%2FA6lj%2FKFEagAdA%2FncMqZqfk%3D",
    //             "filedescription": "File from S3 AWS",
    //             "filename": "Snake_River_(5mb).jpg",
    //             "fileextension": "image/jpeg",
    //             "environment": "reltest",
    //             "securityLevel": "IKKESENSITIV",
    //             "owner": "SKADESFORSIKRING-SKADE",
    //             "customernumber": "2845402"
    //         }
    //     ]
    const files: StageFileResponse[] = await stageFiles(topConfig, taskIds);

    const stagedFiles: StagedFile[] = files.map((file) => {
        const filename = decodeURIComponent(file.filename);
        const { osirFileId, url } = file;

        return {
            name: filename,
            description: filename,
            referenceNr: osirFileId,
            url
        };
    });

    return stagedFiles;
};

UploadDocumentation.getValue = (formValues: FormValues, elementId: string): UploadDocumentationValueType => {
    return formValues[elementId];
};

UploadDocumentation.getValueOrDefault = (formValues: FormValues, elementId: string): UploadDocumentationValueType => {
    const value = UploadDocumentation.getValue(formValues, elementId);

    if (value !== undefined) {
        return value;
    }

    return {
        customerDidNotUploadReason: '',
        taskId: uuid(),
        files: [],
        isFileProcessing: false,
        stateUpdated: '',
        updateEvent: ''
    };
};

UploadDocumentation.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = UploadDocumentation.getValue(formValues, elementId);

    const valueStr =
        value.files.length > 0
            ? value.files.join(', ')
            : value.customerDidNotUploadReason !== ''
            ? value.customerDidNotUploadReason
            : 'Ingen filer er uploadet';

    return valueStr;
};

UploadDocumentation.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    const value: UploadDocumentationValueType = UploadDocumentation.getValueOrDefault(formValues, elementId);
    const element = getElementFromElementId(formConfig, elementId) as UploadDocumentationType;

    const uploadIsRequried = getUploadIsRequiredByFormIdAndValue(formConfig, formValues);

    // If numberOfFiles validator exist do the validation check
    const numberOfFilesValidator: Validator | undefined = element.validators.find(
        (validator: Validator) => validator.type === 'numberOfFiles'
    ) as NumberOfFiles | undefined;

    if (numberOfFilesValidator && uploadIsRequried) {
        const { minFiles, maxFiles } = numberOfFilesValidator;
        if (value.files.length < minFiles || (maxFiles && value.files.length > maxFiles)) {
            return false;
        }
    }

    if (value.isFileProcessing) {
        return false;
    }

    if (value.customerDidNotUploadReason !== '') {
        return true;
    }

    if (value.files.length > 0) {
        return true;
    }

    return false;
};

// TODO
//  - Modify this when the way taskIds are saved is changed in appState
const findTaskIds = (values: Record<string, { taskId: string }>) => {
    const taskIds = Object.values(values)
        .filter((value) => !!value.taskId)
        .map((value) => value.taskId);

    return taskIds;
};

export default UploadDocumentation;
